import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import clsx from 'clsx';

import { makeStyles } from '@material-ui/core/styles';

import {
  priEprsList,
  priEprsCampaignList,
  setPrieprsCampaign,
} from 'lib/api/adver';
import { showSuccessSnackbar } from 'modules/snackbar';
import { addAllList } from 'modules/common';

import Icon from 'components/atoms/Icon';
import NewInput from 'components/atoms/NewInput';
import NewButton from 'components/molecules/NewButton';
import MbRadioGroup from 'components/molecules/MbRadioGroup';
import NewTableContainer from 'components/molecules/NewTable';
import NewFilterWrap from 'components/molecules/NewFilterWrap';
import Modal from 'components/organisms/NewModal';
import NewCommonFilter from 'components/organisms/NewCommonFilter';
import FilterCollection from 'components/organisms/FilterCollection';

const useStyles = makeStyles((theme) => ({
  active: {
    backgroundColor: '#79bd2f',
    '& svg': {
      color: '#fff',
    },
  },
}));

const PriEprs = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  let initParams = {
    sort: 'desc',
    order: 'LAST_DATE',
    priEprsFlag: '01',
  };

  const [initialization, setInitialization] = useState(false);
  const [params, setParams] = useState(initParams);
  const [tempParams, setTempParams] = useState(initParams);
  const initModal = {
    open: false,
    params: {
      sCode: '',
      state: '',
    },
  };
  const [modal, setModal] = useState(initModal);
  const [reFetch, setReFetch] = useState(false);

  const priEprsFlagList = [
    { label: '적용', value: '01' },
    { label: '해제', value: '02' },
  ];

  useEffect(() => {
    if (initialization === true) {
      setInitialization(false);
    }
    if (reFetch === true) {
      setReFetch(false);
    }
  }, [initialization, reFetch]);

  const customized = {
    W_STATE_STR: {
      makeBody: (rowObj, headsMap) => {
        return (
          !!rowObj['W_STATE_STR'] && (
            <Icon
              icon="faPowerOff"
              style={
                rowObj['W_STATE_STR'] === 'Y'
                  ? { color: '#79bd2f' }
                  : { color: '#6d6d6d' }
              }
            />
          )
        );
      },
    },
    M_STATE_STR: {
      makeBody: (rowObj, headsMap) => {
        return (
          !!rowObj['M_STATE_STR'] && (
            <Icon
              icon="faPowerOff"
              style={
                rowObj['M_STATE_STR'] === 'Y'
                  ? { color: '#79bd2f' }
                  : { color: '#6d6d6d' }
              }
            />
          )
        );
      },
    },
    ADVRTS_TP_CODE: { merged: true },
    ADVRTS_PRDT_CODE: { merged: true },
    PRI_EPRS_FLAG_STR: {
      makeBody: (rowObj, headsMap) => {
        return !!rowObj['PRI_EPRS_FLAG_STR'] ? (
          <NewButton
            name="delete"
            title={rowObj['PRI_EPRS_FLAG_STR'] === 'Y' ? t('해제') : t('노출')}
            iconBtn
            icon="faPowerOff"
            disableRipple
            onClick={(e) =>
              handleSetCampaign(
                rowObj['SITE_CODE'],
                rowObj['PRI_EPRS_FLAG_STR'] === 'Y' ? '02' : '01'
              )
            }
            className={clsx(
              rowObj['PRI_EPRS_FLAG_STR'] === 'Y' && classes.active
            )}
          />
        ) : (
          '-'
        );
      },
    },
    LAST_DATE: {
      makeBody: (rowObj, headsMap) => {
        return !!rowObj['LAST_DATE'] ? rowObj['LAST_DATE'] : '-';
      },
    },
  };

  const handleOnReset = () => {
    if (
      JSON.stringify(params) !== JSON.stringify(initParams) ||
      JSON.stringify(tempParams) !== JSON.stringify(initParams)
    ) {
      setInitialization(true);
      setParams(initParams);
      setTempParams(initParams);
    }
  };

  const getParam = () => {
    if (JSON.stringify(params) !== JSON.stringify(tempParams)) {
      setParams(tempParams);
    }
  };

  const handleTempParams = (newParams, name) => {
    let newTempParams = { ...newParams };
    if (name === 'priEprsFlag') {
      if (!newTempParams['adverId']) {
        handleClickSnackbar({
          message: t('해제하기 위해서는 광고주 ID를 입력해주셔야합니다.'),
          option: {
            type: 'error',
          },
        });
        return;
      }
    }
    if (name === 'adverId') {
      if (!newTempParams['adverId']) {
        newTempParams['priEprsFlag'] = '01';
      }
    }
    if (name === 'siteCode' || name === 'adverId') {
      if (!!newTempParams['siteCode']) {
        newTempParams['priEprsFlag'] = 'all';
      } else {
        newTempParams['priEprsFlag'] = '01';
      }
    }
    if (JSON.stringify(tempParams) !== JSON.stringify(newTempParams)) {
      setTempParams(newTempParams);
    }
  };

  const handleButtonOnClick = () => {
    let newParams = { ...tempParams };
    if (!newParams['siteCode']) {
      delete newParams['siteCode'];
    }
    if (!newParams['adverId']) {
      delete newParams['adverId'];
    }
    if (JSON.stringify(newParams) !== JSON.stringify(params)) {
      setParams(newParams);
    }
  };

  const handleSetCampaign = (sCode, state) => {
    setPrieprsCampaign({
      siteCodes: sCode,
      state: state,
    })
      .then((response) => {
        const { message } = response.data;
        handleClickSnackbar({
          message: message,
          option: {
            type: 'success',
          },
        });
      })
      .catch((error) => {
        handleClickSnackbar({
          message: error,
          option: {
            type: 'error',
          },
        });
      })
      .finally(() => {
        setReFetch(true);
      });
  };

  const handleClickSnackbar = useCallback((snackbarOptions) => {
    dispatch(showSuccessSnackbar(snackbarOptions));
    //eslint-disable-next-line
  }, []);

  const handleCheckBtnController = (type, data) => {
    let tempModal = { ...initModal };
    let tmpExlNo = [];
    data.forEach(function (rowData) {
      tmpExlNo.push(rowData['SITE_CODE']);
    });
    tempModal = {
      ...tempModal,
      open: true,
      params: {
        ...tempModal.params,
        sCode: String(tmpExlNo),
      },
    };
    setModal(tempModal);
  };

  const handleModalClose = () => {
    setModal({
      ...modal,
      open: false,
    });
  };

  const handleSubmit = (sCode, state) => {
    handleSetCampaign(sCode, state);
    setModal({
      ...modal,
      open: false,
    });
  };

  const handleModalGetParams = (e) => {
    const { value } = e.target;
    setModal({
      ...modal,
      params: {
        ...modal.params,
        state: value,
      },
    });
  };

  return (
    <div
      style={{
        backgroundColor: '#fff',
        padding: 15,
        borderRadius: 4,
      }}
    >
      <NewFilterWrap handleOnReset={handleOnReset}>
        <FilterCollection
          label={t('적용여부')}
          name="priEprsFlag"
          params={tempParams}
          value={tempParams.priEprsFlag}
          type="radio"
          list={addAllList(priEprsFlagList, 'label', 'value', 'all')}
          callBack={handleTempParams}
          labelKey="label"
          valueKey="value"
        />
        <NewCommonFilter
          initialization={initialization}
          getParam={handleTempParams}
          params={tempParams}
          useDate={false}
          useExternalFilter={false}
          usePltfomTpCodeFilter={false}
          displayFilterList={{
            '01': {
              '01': 1,
              '19': 1,
            },
            '03': {
              '01': 1,
              '19': 1,
            },
          }}
        />
        <NewInput
          label={t('광고주ID')}
          type="filter"
          name="adverId"
          params={tempParams}
          value={tempParams['adverId']}
          callBack={handleTempParams}
        />
        <NewInput
          label={t('SC코드')}
          type="filter"
          name="siteCode"
          params={tempParams}
          value={tempParams['siteCode']}
          callBack={handleTempParams}
        />
        <NewButton onClick={handleButtonOnClick}>{t('검색')}</NewButton>
      </NewFilterWrap>
      <NewTableContainer
        dataReqPromise={
          !!params['adverId'] || !!params['siteCode']
            ? priEprsCampaignList
            : priEprsList
        }
        params={params}
        getParam={getParam}
        customized={customized}
        useFetch={false}
        useCheckBox
        useTotal={false}
        headCallBack={handleCheckBtnController}
        reFetch={reFetch}
        useSearch={false}
      />
      <Modal
        open={modal.open}
        headerTitle={modal.headerTitle}
        confirmBtnTitle={modal.confirmBtnTitle}
        confirmBtnOpen
        // useForm
        handleClose={handleModalClose}
        confirmOnClick={(f) =>
          handleSubmit(modal.params.sCode, modal.params.state)
        }
      >
        <div style={{ display: 'flex' }}>
          <p style={{ marginRight: 5, fontWeight: 'bold' }}>
            {t('우선노출 여부')} :{' '}
          </p>
          <MbRadioGroup
            selectedValue={modal.params.state}
            radioList={priEprsFlagList}
            onChange={handleModalGetParams}
            name="state"
            style={{ marginLeft: -10 }}
          />
        </div>
      </Modal>
    </div>
  );
};

export default PriEprs;
